import { useState } from 'react'
import { invoicesService } from '@/services/invoices-service'

interface SendInvoiceModalProps {
  invoiceId: string
  invoiceNumber: string | null
  clientEmail: string
  open: boolean
  onClose: () => void
  onSent: () => void
}

export function SendInvoiceModal({
  invoiceId,
  invoiceNumber,
  clientEmail,
  open,
  onClose,
  onSent,
}: SendInvoiceModalProps) {
  const [emails, setEmails] = useState<string[]>([clientEmail])
  const [newEmail, setNewEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) return null

  const handleAddEmail = () => {
    if (newEmail.trim() && !emails.includes(newEmail.trim())) {
      setEmails([...emails, newEmail.trim()])
      setNewEmail('')
    }
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email !== emailToRemove))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (emails.length === 0) {
      setError('At least one email recipient is required')
      return
    }

    setIsLoading(true)

    try {
      await invoicesService.send(invoiceId, emails)
      onSent()
      handleClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invoice')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setEmails([clientEmail])
    setNewEmail('')
    setError(null)
    onClose()
  }

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  const handleNewEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddEmail()
    }
  }

  return (
    <div className="inv-modal-overlay" onMouseDown={handleBackdropMouseDown} onKeyDown={handleKeyDown}>
      <div className="inv-modal">
        <div className="inv-modal-header">
          <h2>Send Invoice</h2>
          <button
            onClick={handleClose}
            className="inv-modal-close"
            aria-label="Close"
            type="button"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="inv-modal-body">
          {error && (
            <div className="inv-modal-error" role="alert">
              {error}
            </div>
          )}

          <div className="inv-modal-info">
            <p>
              <strong>Invoice:</strong> {invoiceNumber}
            </p>
          </div>

          <div className="inv-modal-field">
            <label className="inv-modal-label">Recipients</label>
            <div className="inv-modal-email-list">
              {emails.map((email) => (
                <div key={email} className="inv-modal-email-tag">
                  <span>{email}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(email)}
                    className="inv-modal-email-remove"
                    aria-label={`Remove ${email}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="inv-modal-field">
            <label htmlFor="add-email" className="inv-modal-label">
              Add Another Email
            </label>
            <div className="inv-modal-email-input-group">
              <input
                id="add-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={handleNewEmailKeyDown}
                placeholder="Email address"
                className="inv-modal-input"
              />
              <button
                type="button"
                onClick={handleAddEmail}
                className="inv-modal-btn-small"
                disabled={!newEmail.trim()}
              >
                Add
              </button>
            </div>
          </div>
        </form>

        <div className="inv-modal-footer">
          <button
            type="button"
            onClick={handleClose}
            className="inv-modal-btn inv-modal-btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="inv-modal-btn inv-modal-btn-primary"
            disabled={isLoading || emails.length === 0}
          >
            {isLoading ? 'Sending...' : 'Send Invoice'}
          </button>
        </div>
      </div>
    </div>
  )
}
